/** SUNTRONIX ONE — Data Quality & Reconciliation Control Centre v1.
 *
 *  Suntronix-internal admin view.  Shows the live count of every one of
 *  the 8 data anomalies, lets an operator drill in per-check, and
 *  re-run the scan on demand.  History strip surfaces the trend from
 *  the nightly cron so a spike is impossible to miss.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  RefreshCcw, PlayCircle, ShieldAlert, Layers, Search,
  AlertTriangle, ChevronRight, Filter, GitMerge, Mail,
} from "lucide-react";
import MergeDuplicatesDialog from "@/components/app/MergeDuplicatesDialog";

const SEV_TONE = {
  high:   { pill: "bg-red-50 text-red-700 border-red-200",
            dot:  "bg-red-500" },
  medium: { pill: "bg-amber-50 text-amber-700 border-amber-200",
            dot:  "bg-amber-500" },
  low:    { pill: "bg-slate-100 text-slate-700 border-slate-200",
            dot:  "bg-slate-400" },
};

function SeverityPill({ severity }) {
  const t = SEV_TONE[severity] || SEV_TONE.low;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${t.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
      {severity}
    </span>
  );
}

export default function DataQualityControl() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rowFilter, setRowFilter] = useState("");
  const [mcFilter, setMcFilter] = useState("");
  const [mergeSeed, setMergeSeed] = useState(null);
  const [mergeSiblings, setMergeSiblings] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);

  const openMerge = (assetId, dupValue) => {
    // Include every other sample row sharing the same duplicate value so
    // the wizard can offer the full cluster.
    const siblings = (selectedIssue?.samples || [])
      .filter((r) => r._duplicate_value === dupValue && r.id !== assetId)
      .map((r) => r.id);
    setMergeSeed(assetId);
    setMergeSiblings(siblings);
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/data-quality/digest/send-weekly");
      toast.success(`Digest sent · ${data.spikes.length} spike(s) to `
                       + `${data.recipients.length} recipient(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest failed");
    } finally { setSendingDigest(false); }
  };

  const isSuntronix = user && !user.master_company_id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/data-quality/issues");
      setData(data);
      if (!selected && (data.issues || []).length) {
        // Auto-select the first check that has issues so the operator
        // sees actionable rows immediately.
        const firstWithIssues = data.issues.find((i) => i.count > 0);
        setSelected(firstWithIssues?.code || data.issues[0].code);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load data quality issues");
    } finally { setLoading(false); }
  }, [selected]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/data-quality/history", { params: { limit: 14 } });
      setHistory(data.items || []);
    } catch { /* history is optional */ }
  }, []);

  useEffect(() => {
    if (!isSuntronix) return;
    load();
    loadHistory();
  }, [isSuntronix, load, loadHistory]);

  const runScan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/data-quality/scan");
      toast.success(`Scan complete · ${data.summary.total_issues} issues across ${data.summary.checks_with_issues} check(s)`);
      setData({
        generated_at: data.run_at,
        summary: data.summary,
        issues: data.issues,
      });
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally { setRunning(false); }
  };

  const selectedIssue = useMemo(() => {
    if (!data || !selected) return null;
    return (data.issues || []).find((i) => i.code === selected) || null;
  }, [data, selected]);

  const filteredSamples = useMemo(() => {
    if (!selectedIssue) return [];
    const rows = selectedIssue.samples || [];
    const q = rowFilter.trim().toLowerCase();
    const mc = mcFilter.trim().toLowerCase();
    return rows.filter((r) => {
      const hay = [
        r.asset_tag, r.serial, r.lifecycle_state,
        r.master_company_id, r.master_company_name,
        r.current_employee_id, r.data_erasure_status,
      ].map((x) => (x || "").toString().toLowerCase()).join(" ");
      if (q && !hay.includes(q)) return false;
      if (mc) {
        const mcHay = `${r.master_company_id || ""} ${r.master_company_name || ""}`.toLowerCase();
        if (!mcHay.includes(mc)) return false;
      }
      return true;
    });
  }, [selectedIssue, rowFilter, mcFilter]);

  const exportCsv = () => {
    if (!selectedIssue?.samples?.length) return;
    const header = ["Check", "Asset Tag", "Serial", "Lifecycle",
                     "Master Company", "Master Company ID",
                     "Employee ID", "Warehouse", "Location",
                     "Erasure Status", "Purchase Date", "Warranty End"];
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = [header.join(",")];
    for (const r of filteredSamples) {
      lines.push([
        selectedIssue.code, r.asset_tag, r.serial, r.lifecycle_state,
        r.master_company_name, r.master_company_id,
        r.current_employee_id, r.current_warehouse_id, r.current_location_id,
        r.data_erasure_status, r.purchase_date, r.warranty_end,
      ].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `data_quality_${selectedIssue.code}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isSuntronix) {
    return (
      <div className="max-w-lg mx-auto mt-16 st-panel p-6 text-center"
            data-testid="data-quality-forbidden">
        <ShieldAlert className="h-8 w-8 mx-auto text-slate-400 mb-2" />
        <div className="font-heading text-lg mb-1">Suntronix internal only</div>
        <div className="text-sm text-slate-500">
          The Data Quality Control Centre is restricted to Suntronix operators.
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const issues = data?.issues || [];
  const generatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleString()
    : null;

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="data-quality-page">
      <PageHeader
        eyebrow="Admin · Reconciliation"
        title="Data Quality Control Centre"
        context="Every anomaly in the Golden Asset Record — duplicates, orphans, unfinished workflows — surfaced in one screen so the ops team can reconcile before month-end."
        actions={
          <div className="flex items-center gap-2">
            {generatedAt && (
              <div className="text-[11px] text-slate-500 mr-1">
                Scanned {generatedAt}
              </div>
            )}
            <Button variant="outline" onClick={sendDigest} disabled={sendingDigest}
              data-testid="data-quality-send-digest">
              <Mail className={`h-3.5 w-3.5 mr-1.5 ${sendingDigest ? "animate-pulse" : ""}`} />
              {sendingDigest ? "Sending…" : "Send digest"}
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}
              data-testid="data-quality-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={runScan} disabled={running}
              data-testid="data-quality-run-scan">
              <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
              {running ? "Running…" : "Run scan"}
            </Button>
          </div>
        }
        testId="data-quality-header"
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "total", label: "Total open issues",
            value: summary.total_issues ?? 0,
            tint: (summary.total_issues || 0) > 0 ? "text-red-700" : "text-emerald-700",
            icon: AlertTriangle },
          { key: "high", label: "High severity",
            value: summary.high_severity_issues ?? 0,
            tint: (summary.high_severity_issues || 0) > 0 ? "text-red-700" : "text-slate-900",
            icon: ShieldAlert },
          { key: "medium", label: "Medium severity",
            value: summary.medium_severity_issues ?? 0,
            tint: "text-amber-700", icon: Layers },
          { key: "with", label: "Checks with issues",
            value: `${summary.checks_with_issues ?? 0} / ${summary.checks_run ?? 8}`,
            tint: "text-slate-900", icon: Filter },
        ].map((k) => (
          <div key={k.key} className="st-panel p-3"
            data-testid={`data-quality-kpi-${k.key}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              <k.icon className="h-3 w-3" />
              {k.label}
            </div>
            <div className={`text-2xl font-heading font-semibold mt-1 tabular-nums ${k.tint}`}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Trend from cron history */}
      {history.length > 1 && (
        <Section title="14-day trend"
          hint="Nightly cron snapshots · higher bar = more open issues"
          testId="data-quality-trend">
          <div className="st-panel p-3">
            <div className="flex items-end gap-2 h-24">
              {history.slice().reverse().map((h) => {
                const total = h.summary?.total_issues || 0;
                const max = Math.max(1, ...history.map((x) => x.summary?.total_issues || 0));
                const pct = Math.round((total / max) * 100);
                return (
                  <div key={h.id} className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-slate-200 rounded-t" style={{ height: `${Math.max(pct, 4)}%` }}
                          title={`${new Date(h.run_at).toLocaleString()} · ${total} issues`}>
                    </div>
                    <div className="text-[9px] text-slate-500 mt-1 tabular-nums">{total}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      )}

      {/* Two column: check list + drill-in */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        <Section title="Checks" testId="data-quality-check-list">
          <div className="st-panel overflow-hidden">
            {loading && !issues.length ? (
              <div className="text-slate-400 text-[12px] text-center py-6">Scanning…</div>
            ) : issues.map((i) => (
              <button
                key={i.code}
                onClick={() => setSelected(i.code)}
                data-testid={`data-quality-check-${i.code}`}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex items-center gap-2 hover:bg-slate-50 transition
                            ${selected === i.code ? "bg-slate-50" : ""}`}>
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${SEV_TONE[i.severity]?.dot || "bg-slate-400"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-slate-900 truncate">{i.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{i.severity} · {i.code}</div>
                </div>
                <span className={`text-[13px] font-semibold tabular-nums ${i.count > 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {i.count}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
              </button>
            ))}
          </div>
        </Section>

        <Section
          title={selectedIssue?.title || "Select a check"}
          hint={selectedIssue?.description}
          testId="data-quality-issue-detail"
          action={selectedIssue && (
            <div className="flex items-center gap-2">
              <SeverityPill severity={selectedIssue.severity} />
              <Button size="sm" variant="outline" onClick={exportCsv}
                disabled={!filteredSamples.length}
                data-testid="data-quality-export">
                Export CSV
              </Button>
            </div>
          )}>
          {!selectedIssue && (
            <div className="st-panel p-6 text-center text-slate-400 text-sm">
              Select a check on the left to see offending assets.
            </div>
          )}
          {selectedIssue && (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Filter rows (tag, serial, lifecycle…)"
                    value={rowFilter}
                    onChange={(e) => setRowFilter(e.target.value)}
                    className="pl-8 h-9 text-[13px]"
                    data-testid="data-quality-row-filter"
                  />
                </div>
                <Input
                  placeholder="Filter by customer"
                  value={mcFilter}
                  onChange={(e) => setMcFilter(e.target.value)}
                  className="h-9 text-[13px] max-w-[220px]"
                  data-testid="data-quality-mc-filter"
                />
                <div className="text-[11px] text-slate-500">
                  Showing {filteredSamples.length} of {selectedIssue.samples?.length || 0}
                  {selectedIssue.count > (selectedIssue.samples?.length || 0) && (
                    <> · total {selectedIssue.count} — first {selectedIssue.samples?.length || 0} samples</>
                  )}
                </div>
              </div>

              {selectedIssue.count === 0 ? (
                <div className="st-panel p-6 text-center text-emerald-700 text-sm"
                      data-testid="data-quality-clean">
                  No issues — this check is clean.
                </div>
              ) : (
                <div className="st-panel overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2">Asset</th>
                        <th className="text-left px-3 py-2">Serial</th>
                        <th className="text-left px-3 py-2">Lifecycle</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">Detail</th>
                        <th className="text-right px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSamples.map((r) => (
                        <tr key={r.id + (r._duplicate_value || "")}
                            className="border-t border-slate-100 hover:bg-slate-50"
                            data-testid={`data-quality-row-${r.id}`}>
                          <td className="px-3 py-2 font-mono text-[12px]">{r.asset_tag || "—"}</td>
                          <td className="px-3 py-2 font-mono text-[12px]">{r.serial || "—"}</td>
                          <td className="px-3 py-2 text-[12px]">{r.lifecycle_state || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="text-[12px]">{r.master_company_name || <span className="text-slate-400">—</span>}</div>
                            {r.master_company_id && (
                              <div className="text-[10px] font-mono text-slate-400">{r.master_company_id.slice(0, 8)}…</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-[12px] text-slate-600">
                            {selectedIssue.code === "duplicate_serial" && r._duplicate_value && (
                              <span>Duplicate value: <code className="font-mono">{r._duplicate_value}</code></span>
                            )}
                            {selectedIssue.code === "duplicate_asset_tag" && r._duplicate_value && (
                              <span>Duplicate value: <code className="font-mono">{r._duplicate_value}</code></span>
                            )}
                            {selectedIssue.code === "inactive_employee_assign" && (
                              <span>Emp {r.assignee_name || r.current_employee_id} (inactive)</span>
                            )}
                            {selectedIssue.code === "returned_without_erasure" && (
                              <span>Erasure: <b>{r.data_erasure_status || "—"}</b></span>
                            )}
                            {selectedIssue.code === "in_warehouse_no_location" && (
                              <span>No warehouse / location set</span>
                            )}
                            {selectedIssue.code === "missing_purchase_warranty" && (
                              <span>
                                {r.purchase_date ? "" : "Purchase missing"}
                                {(!r.purchase_date && !r.warranty_end) ? " · " : ""}
                                {r.warranty_end ? "" : "Warranty missing"}
                              </span>
                            )}
                            {selectedIssue.code === "missing_customer" && (
                              <span>Unknown master company</span>
                            )}
                            {selectedIssue.code === "in_use_without_employee" && (
                              <span>Assigned to no one</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(selectedIssue.code === "duplicate_serial"
                              || selectedIssue.code === "duplicate_asset_tag") && (
                              <Button size="sm" variant="outline"
                                onClick={() => openMerge(r.id, r._duplicate_value)}
                                data-testid={`data-quality-merge-${r.id}`}>
                                <GitMerge className="h-3 w-3 mr-1" /> Merge
                              </Button>
                            )}
                            <Button size="sm" variant="ghost"
                              onClick={() => navigate(`/inventory/passport/${r.id}`)}
                              data-testid={`data-quality-open-${r.id}`}>
                              Open passport
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {filteredSamples.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-[12px]">
                            No rows match your filter.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Section>
      </div>

      <MergeDuplicatesDialog
        open={!!mergeSeed}
        onOpenChange={(v) => { if (!v) { setMergeSeed(null); setMergeSiblings([]); } }}
        seedAssetId={mergeSeed}
        siblingAssetIds={mergeSiblings}
        onMerged={() => { setMergeSeed(null); setMergeSiblings([]); load(); }}
      />
    </div>
  );
}
